/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { LogProvider, TrouterState } from './Interfaces';
import { Logger } from './Logger';
import { ReconnectReason } from './TrouterConnection';

export interface IManagerWorker {
    startFirstConnection();
    stopFirstConnection(dontUnregister: boolean);

    startSecondConnection(useConnectParamsFromCache: boolean);
    stopSecondConnection(dontUnregister: boolean);
    stopSecondConnectionDelayed();

    forceStopLingeringConnection();

    switchConnections();
    doesSecondConnectionExist(): boolean;

    dispatchConnected();
    dispatchReconnecting();
    dispatchStopped();
    dispatchRegistrationState(isRegistered: boolean);
}

/**
 * State machine for manager
 */
export class TrouterManagerFsm {
    private logger: Logger;
    private state: TrouterState = TrouterState.Unknown;

    constructor(logFunc: LogProvider, private worker: IManagerWorker) {
        this.logger = new Logger('ManagerFsm', logFunc);
    }

    public start() {
        if (this.state === TrouterState.Unknown) {
            this.setState(TrouterState.Disconnected);
            this.worker.forceStopLingeringConnection();
            this.worker.startFirstConnection();
        } else {
            this.showIgnored('start');
        }
    }

    public stop(dontUnregister?: boolean) {
        if (this.state !== TrouterState.Unknown) {
            this.setState(TrouterState.Unknown);
            this.worker.stopFirstConnection(dontUnregister === true);
            this.worker.stopSecondConnection(dontUnregister === true);
        } else {
            this.showIgnored('stop');
        }
    }

    /** Returns current state */
    public getState(): TrouterState {
        return this.state;
    }

    public onConnected(first: boolean) {
        if (this.state === TrouterState.Disconnected && first) {
            if (this.worker.doesSecondConnectionExist()) {
                this.setState(TrouterState.Switching);
            } else {
                this.setState(TrouterState.Connected);
                this.worker.dispatchConnected();
            }
        } else {
            this.showIgnored(`onConnected(${first})`);
        }
    }

    public onRegistered(first: boolean) {
        if (this.state === TrouterState.Disconnected && !first) {
            this.setState(TrouterState.Connected);
            this.worker.switchConnections();
            this.worker.stopSecondConnection(true);
            this.worker.dispatchConnected();
        } else if (this.state === TrouterState.Switching && !first) {
            this.setState(TrouterState.Connected);
            this.worker.switchConnections();
            this.worker.stopSecondConnectionDelayed();
            this.worker.dispatchConnected();
        }
        this.worker.dispatchRegistrationState(true);
    }

    public onUnregistered(first: boolean) {
        if (first) {
            this.worker.dispatchRegistrationState(false);
        }
    }

    public onReconnecting(first: boolean) {
        if (this.state === TrouterState.Connected && first) {
            this.setState(TrouterState.Disconnected);
            this.worker.dispatchReconnecting();
        } else if (this.state === TrouterState.Switching && first) {
            this.setState(TrouterState.Disconnected);
            this.worker.dispatchReconnecting();
        } else {
            this.showIgnored(`onReconnecting(${first})`);
        }
    }

    public onReconnectionRequired(first: boolean, useConnectParamsFromCache: boolean, reason: ReconnectReason) {
        if (this.state === TrouterState.Connected && first) {
            this.setState(TrouterState.Switching);
            this.worker.startSecondConnection(useConnectParamsFromCache);
        } else if (this.state === TrouterState.Disconnected && first) {
            this.worker.startSecondConnection(useConnectParamsFromCache);
        } else if (this.state === TrouterState.Switching && first && reason === ReconnectReason.Configuration) {
            // Intentionally ignoring `trouter.reconnect` events arriving during an in-progress switch (on either connection),
            // only configuration change is handled. This matches the current behavior of the C++ client.
            // Even in the ignored cases, the client should still end up being connected to the right environment eventually.
            this.logger.debug('onReconnectionRequired: switch requested while already in Switching state');
            this.worker.stopSecondConnection(true);
            this.worker.startSecondConnection(useConnectParamsFromCache);
        } else {
            this.showIgnored(`onReconnectionRequired(${first})`);
        }
    }

    public onDisconnected(first: boolean) {
        if (this.state === TrouterState.Unknown && first) {
            this.worker.dispatchStopped();
        } else {
            this.showIgnored(`onDisconnected(${first})`);
        }
    }

    private showIgnored(event: string) {
        this.logger.info(`Ignoring event '${event}' in state '${TrouterState[this.state]}'`);
    }

    private setState(state: TrouterState) {
        this.logger.info(`Switching from state '${TrouterState[this.state]}' to state '${TrouterState[state]}'`);
        if (this.state === state) {
            this.logger.error(`Attempt to switching into the current state '${TrouterState[state]}'`);

            return;
        }
        this.state = state;
    }
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterManagerFsm.ts